/* eslint-disable react/no-danger */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Empty, Select, Spin } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand, faMinimize } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import GETJwtToken from "../../../redux/authentication/authentication";
import { useLazyGetMapViewQuery } from "../../../redux/mapView/mapViewAPI";
import { RootState } from "../../../app/store";
import MonitoringHeader from "../monitoring/MonitoringHeader";
import MonitoringMapViewWrapper from "../mapView/MonitoringMapViewWrapper";
import { BankMessagesProvider } from "../socketMessageState/BankMessagesContext";
import formatUuidT from "../../../flatbuffers/display/uuid";
import {
  updateMapViewArray,
  updateMapViewItem,
  updateSelectedOrganization,
} from "../../../redux/mapView/mapViewSlice";
import { resolveMapViewMonitorOrganizationId } from "../monitoring/mapViewOrgResolution";
import SvgController from "../../mapViewManagement/mapEditor/SvgController";
import { extractMapRenderData, resolveMapCoordinate } from "../mapView/mapRenderData";
import MapDisplayLegend from "../mapView/MapDisplayLegend";

function resolvePublicAssetUrl(path: string): string {
  if (!path) {
    return path;
  }

  if (/^(https?:)?\/\//i.test(path) || path.startsWith("data:")) {
    return path;
  }

  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${normalizedPath}`;
}

function safeJsonStringify(value: unknown): string {
  const seen = new WeakSet<object>();

  try {
    const json = JSON.stringify(
      value,
      (_key, currentValue) => {
        if (typeof currentValue === "bigint") {
          return currentValue.toString();
        }

        if (currentValue instanceof Uint8Array) {
          return Array.from(currentValue);
        }

        if (currentValue && typeof currentValue === "object") {
          if (seen.has(currentValue)) {
            return "[Circular]";
          }
          seen.add(currentValue);
        }

        return currentValue;
      },
      2,
    );

    return json ?? "null";
  } catch {
    return JSON.stringify({ error: "Unable to serialize map data" });
  }
}

function isSameMapItem(a: any, b: any): boolean {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }

  return String(a?.id ?? "") === String(b?.id ?? "");
}

function MapViewMonitor() {
  const dispatch = useDispatch();
  const [triggerGetMapView, { data: dataGetMapView, isLoading: isLoadingGetMapView }] =
    useLazyGetMapViewQuery();
  const { globalOrganizationId, userLogged, theme } = useSelector((state: RootState) => state.home);
  const { organizationSelectMultipleData } = useSelector((state: RootState) => state.organization);
  const {
    selectedOrganization: mapViewSelectedOrganization,
    mapViewArray,
    mapViewItem,
  } = useSelector((state: RootState) => state.mapView);

  const [selectedMapId, setSelectedMapId] = useState<string>("");
  const [svgSrc, setSvgSrc] = useState<string>("");
  const mapViewportRef = useRef<HTMLDivElement | null>(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);

  const resolvedOrganizationId = useMemo(
    () =>
      resolveMapViewMonitorOrganizationId(
        globalOrganizationId,
        userLogged,
        mapViewSelectedOrganization,
        Array.isArray(organizationSelectMultipleData) ? organizationSelectMultipleData : [],
      ),
    [globalOrganizationId, mapViewSelectedOrganization, organizationSelectMultipleData, userLogged],
  );

  const mapViews = useMemo(() => {
    const rows = Array.isArray(dataGetMapView?.data) ? dataGetMapView.data : [];
    return rows.map((item: any) => ({
      ...item,
      id: formatUuidT(item?.id),
      name: String(item?.name || ""),
      has_display_view: Boolean(
        item?.has_display_view ?? item?.hasDisplayView ?? item?.main ?? false,
      ),
    }));
  }, [dataGetMapView]);

  const monitoringLayouts = useMemo(
    () =>
      (Array.isArray(organizationSelectMultipleData) ? organizationSelectMultipleData : []).filter(
        (layout: any) => layout?.visible !== false,
      ),
    [organizationSelectMultipleData],
  );

  const selectedMap = useMemo(
    () => mapViews.find((map: any) => map.id === selectedMapId) || mapViews[0] || null,
    [mapViews, selectedMapId],
  );
  const showDisplayLegend = Boolean(
    selectedMap?.has_display_view ?? selectedMap?.hasDisplayView ?? selectedMap?.main ?? false,
  );
  const { groupsAndBanks, liveLifts } = useMemo(
    () => extractMapRenderData(selectedMap),
    [selectedMap],
  );

  useEffect(() => {
    if (!resolvedOrganizationId) {
      return;
    }

    if (mapViewSelectedOrganization !== resolvedOrganizationId) {
      dispatch(updateSelectedOrganization(resolvedOrganizationId));
    }

    const getMaps = async () => {
      const token = await GETJwtToken();
      await triggerGetMapView({
        token,
        body: {
          org_id: resolvedOrganizationId,
        },
      });
    };

    getMaps();
  }, [dispatch, mapViewSelectedOrganization, resolvedOrganizationId, triggerGetMapView]);

  useEffect(() => {
    const currentIds = new Set((mapViewArray || []).map((map: any) => String(map?.id ?? "")));
    const nextIds = new Set(mapViews.map((map: any) => String(map?.id ?? "")));
    if (currentIds.size !== nextIds.size || [...currentIds].some((id) => !nextIds.has(id))) {
      dispatch(updateMapViewArray(mapViews as any));
    }
  }, [dispatch, mapViews, mapViewArray]);

  useEffect(() => {
    if (!mapViews.length) {
      setSelectedMapId("");
      return;
    }

    if (!selectedMapId || !mapViews.some((map: any) => map.id === selectedMapId)) {
      const mainMap = mapViews.find(
        (map: any) => map?.has_display_view === true || map?.main === true,
      );
      setSelectedMapId((mainMap || mapViews[0]).id);
    }
  }, [mapViews, selectedMapId]);

  useEffect(() => {
    if (!selectedMap) {
      return;
    }
    if (!isSameMapItem(mapViewItem, selectedMap)) {
      dispatch(updateMapViewItem(selectedMap as any));
    }
  }, [dispatch, selectedMap, mapViewItem]);

  const requestMapFullscreen = useCallback(async () => {
    const element = mapViewportRef.current;
    if (!element) {
      return;
    }

    const isCurrentFullscreenTarget =
      document.fullscreenElement === element ||
      (document as any).webkitFullscreenElement === element ||
      (document as any).msFullscreenElement === element;

    if (isCurrentFullscreenTarget) {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
      return;
    }

    const requestFullscreen = (target: HTMLElement) => {
      if (typeof target.requestFullscreen === "function") {
        return target.requestFullscreen();
      }
      if (typeof (target as any).webkitRequestFullscreen === "function") {
        return (target as any).webkitRequestFullscreen();
      }
      if (typeof (target as any).msRequestFullscreen === "function") {
        return (target as any).msRequestFullscreen();
      }
      return Promise.resolve();
    };

    try {
      await requestFullscreen(element);
    } catch {
      setIsMapFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const element = mapViewportRef.current;
      const isFullscreen =
        document.fullscreenElement === element ||
        (document as any).webkitFullscreenElement === element ||
        (document as any).msFullscreenElement === element;
      setIsMapFullscreen(isFullscreen);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const svgUrl = resolvePublicAssetUrl(
      selectedMap?.map_img_url || selectedMap?.mapImgUrl || selectedMap?.offline_url || "",
    );
    setSvgSrc(svgUrl);
  }, [selectedMap]);

  if (!resolvedOrganizationId) {
    return <Empty description="No organization selected" />;
  }

  return (
    <BankMessagesProvider>
      <div>
        <MonitoringHeader
          hideScheduledActionsButton
          leftContent={
            <Select
              value={selectedMapId || undefined}
              placeholder="Select map"
              style={{ minWidth: 280 }}
              loading={isLoadingGetMapView}
              onChange={(value) => setSelectedMapId(value)}
              options={mapViews.map((map: any) => ({ label: map.name, value: map.id }))}
              showSearch
              optionFilterProp="label"
            />
          }
        />

        {!selectedMap ? (
          <div className={`layout__contentPage__${theme}`} style={{ paddingTop: 8 }}>
            <Spin spinning={isLoadingGetMapView} />
          </div>
        ) : (
          <>
            <div
              ref={mapViewportRef}
              style={{
                position: "relative",
                width: "100%",
                height: "70vh",
                background: "transparent",
                overflow: "hidden",
                contain: "layout style paint",
              }}
            >
              <button
                type="button"
                onClick={requestMapFullscreen}
                aria-label={isMapFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                style={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  zIndex: 2600,
                  border: "none",
                  borderRadius: 999,
                  width: 34,
                  height: 34,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(15, 23, 42, 0.7)",
                  color: "#ffffff",
                  cursor: "pointer",
                  boxShadow: "0 8px 20px rgba(0, 0, 0, 0.22)",
                }}
              >
                <FontAwesomeIcon icon={isMapFullscreen ? faMinimize : faExpand} />
              </button>
              <SvgController
                svgSrc={svgSrc}
                alt={selectedMap?.name || "Map view"}
                showControls={!showDisplayLegend}
                minZoom={0.5}
                maxZoom={5}
                zoomStep={0.2}
                onSvgLoaded={() => {
                  safeJsonStringify(selectedMap);
                }}
                waypoints={[]}
                groupWaypoints={groupsAndBanks}
                renderOverlay={({ width, height }) => (
                  <>
                    {liveLifts.map((pin) => {
                      const x = resolveMapCoordinate(pin.x, width);
                      const y = resolveMapCoordinate(pin.y, height);
                      if (x === null || y === null) {
                        return null;
                      }

                      return (
                        <MonitoringMapViewWrapper
                          key={`${pin.id}-${pin.text}`}
                          liftId={pin.id}
                          text={pin.text}
                          direction={pin.direction}
                          type={pin.type}
                          x={x}
                          y={y}
                          layouts={monitoringLayouts}
                          liftType={pin.liftType}
                          interactive={false}
                          displayName={pin.displayName}
                          scale={pin.scale}
                          backgroundColorHex={pin.backgroundColorHex}
                          backgroundOpacity={pin.backgroundOpacity}
                          titleFontSize={pin.titleFontSize}
                          titleColorHex={pin.titleColorHex}
                          titleRotationDeg={pin.titleRotationDeg}
                          backgroundWidth={pin.backgroundWidth}
                          backgroundHeight={pin.backgroundHeight}
                        />
                      );
                    })}
                  </>
                )}
              />
              {showDisplayLegend ? (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                  }}
                >
                  <MapDisplayLegend overlay />
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>
    </BankMessagesProvider>
  );
}

export default MapViewMonitor;
